import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Form, Button, Modal } from "react-bootstrap";
import { Table, Tag, Space, Spin, Tooltip, Modal as AntModal, Descriptions } from "antd";
import { ToastContainer, toast } from "react-toastify";
import {
  MdHotel,
  MdDownload,
  MdRefresh,
  MdEdit,
  MdAutoFixHigh,
  MdCloudUpload,
  MdErrorOutline,
} from "react-icons/md";
import * as XLSX from "xlsx";
import gestionAlojamientoApi from "../../../services/api/gestionAlojamientoApi";
import { alojamientoProveedorApi } from "../../../services/api/alojamientoProveedorApi";
import {
  bloqueoSincronizacionFila,
  puedeSincronizarFila,
} from "./gestionAlojamientoConfig";
import "react-toastify/dist/ReactToastify.css";

const CAMPO_FECHA_OPTS = [
  { value: "reserva", label: "Fecha reserva" },
  { value: "inicio_estadia", label: "Inicio estadía" },
  { value: "check_in", label: "Check-in" },
  { value: "check_out", label: "Check-out" },
];

const SYNC_PROVEEDOR_OPTS = [
  { value: "", label: "Todos" },
  { value: "sincronizada", label: "Sincronizada (Logiflex)" },
  { value: "no_sincronizada", label: "No sincronizada" },
  { value: "pendiente", label: "Pendiente" },
  { value: "en_cola", label: "En cola / procesando" },
  { value: "error", label: "Con error" },
];

const colorEstado = (nombre) => {
  const k = String(nombre || "").toLowerCase();
  if (k === "reservada") return "blue";
  if (k === "ocupada") return "green";
  if (k === "finalizada") return "purple";
  if (k === "cancelada") return "red";
  return "default";
};

const renderEstadoSyncProveedor = (_, record) => {
  if (record.sincronizada_proveedor) {
    return (
      <Tooltip title={record.fecha_sync_proveedor ? `Enviado: ${record.fecha_sync_proveedor}` : ""}>
        <Tag color="success">Sincronizada</Tag>
      </Tooltip>
    );
  }
  const estado = record.estado_sync_proveedor || "pendiente";
  if (estado === "error") {
    return (
      <Tooltip title={truncarTexto(record.respuesta_sync_proveedor, 180) || "Error de sincronización"}>
        <Tag color="error">Error</Tag>
      </Tooltip>
    );
  }
  if (estado === "procesando") return <Tag color="processing">Procesando</Tag>;
  if (estado === "reintento") {
    return (
      <Tooltip title={truncarTexto(record.respuesta_sync_proveedor, 180) || "Esperando reintento"}>
        <Tag color="orange">Reintento {record.intentos_sync || 0}</Tag>
      </Tooltip>
    );
  }
  if (estado === "en_cola") return <Tag color="blue">En cola</Tag>;
  return <Tag color="default">Pendiente</Tag>;
};

const truncarTexto = (texto, max = 120) => {
  if (!texto) return "";
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const esSyncExitoso = (row) => Boolean(row.sincronizada_proveedor);

const estaEnColaSync = (row) =>
  ["en_cola", "procesando"].includes(row.estado_sync_proveedor);

const puedeReintentarSync = (row) =>
  ["error", "reintento"].includes(row.estado_sync_proveedor);

const puedeEncolarSync = (row, config) =>
  Boolean(row.cama_homologada) &&
  (puedeSincronizarFila(row, config) ||
    puedeReintentarSync(row) ||
    estaEnColaSync(row));

const puedeVerErrorSync = (row) =>
  ["error", "reintento"].includes(row.estado_sync_proveedor);

const renderHomologacionCama = (_, record) => {
  const idProv = (record.cama_id_proveedor || "").trim();
  if (record.cama_homologada && idProv) {
    return (
      <div className="celda-homologacion">
        <Tag color="success" className="mb-0">Homologada</Tag>
        <div className="small text-muted mt-1 celda-id-proveedor" title={idProv}>
          id: {idProv}
        </div>
      </div>
    );
  }
  return (
    <Tooltip title="La cama no tiene id_proveedor. Homologue en Procesos → Homologación Camas Logiflex">
      <Tag color="warning">Sin id_proveedor</Tag>
    </Tooltip>
  );
};

const extraerMensajeError = (error, fallback) => {
  const msg = error.response?.data?.detail || error.response?.data?.message || fallback;
  return typeof msg === "string" ? msg : JSON.stringify(msg);
};

const GestionAlojamientoLogiflex = ({ config }) => {
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");

  const [estados, setEstados] = useState([]);
  const [relacionActivo, setRelacionActivo] = useState("");
  const [filas, setFilas] = useState([]);
  const [resumen, setResumen] = useState({
    total: 0,
    activas: 0,
    inactivas: 0,
    sin_fechas_estadia: 0,
    sincronizadas_proveedor: 0,
    no_sincronizadas_proveedor: 0,
    sin_homologar: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [completando, setCompletando] = useState(false);

  const [estadoId, setEstadoId] = useState("");
  const [activoFiltro, setActivoFiltro] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [campoFecha, setCampoFecha] = useState(config.defaultCampoFecha);
  const [soloSinFechas, setSoloSinFechas] = useState(false);
  const [syncProveedor, setSyncProveedor] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const [seleccionados, setSeleccionados] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [motivoModificacion, setMotivoModificacion] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [enviandoSyncId, setEnviandoSyncId] = useState(null);
  const [sincronizandoLote, setSincronizandoLote] = useState(false);
  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [loadingErrorDetalle, setLoadingErrorDetalle] = useState(false);

  const showToast = useCallback((type, message) => {
    const opts = { position: "bottom-right", autoClose: 3500 };
    if (type === "success") toast.success(message, opts);
    else if (type === "error") toast.error(message, opts);
    else toast.warning(message, opts);
  }, []);

  useEffect(() => {
    gestionAlojamientoApi
      .getEstadosAsignacion()
      .then(({ data }) => {
        const lista = data.data || [];
        setEstados(lista);
        setRelacionActivo(data.relacion_activo_estado || "");
        if (config.defaultEstadoNombre) {
          const match = lista.find(
            (e) => String(e.nombre || "").toLowerCase() === config.defaultEstadoNombre.toLowerCase()
          );
          if (match) setEstadoId(String(match.id));
        }
      })
      .catch(() => {});
  }, [config.defaultEstadoNombre]);

  const cargarListado = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoading(true);
    try {
      const params = { campo_fecha: campoFecha };
      if (estadoId) params.estado_asignacion_id = Number(estadoId);
      if (activoFiltro !== "") params.activo = activoFiltro;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (config.showFechasEstadia && soloSinFechas) params.solo_sin_fechas_estadia = true;
      if (syncProveedor) params.sync_proveedor = syncProveedor;
      if (busqueda.trim()) params.busqueda = busqueda.trim();

      const { data } = await gestionAlojamientoApi.getListado(config.key, selectedProject.id, params);
      setFilas(data.data || []);
      setResumen(
        data.resumen || {
          total: 0,
          activas: 0,
          inactivas: 0,
          sin_fechas_estadia: 0,
          sincronizadas_proveedor: 0,
          no_sincronizadas_proveedor: 0,
          sin_homologar: 0,
        }
      );
      if (data.relacion_activo_estado) setRelacionActivo(data.relacion_activo_estado);
      setSeleccionados([]);
    } catch (error) {
      showToast("error", error.response?.data?.detail || `Error al cargar ${config.entityLabelPlural}`);
    } finally {
      setLoading(false);
    }
  }, [
    selectedProject?.id,
    estadoId,
    activoFiltro,
    fechaDesde,
    fechaHasta,
    campoFecha,
    soloSinFechas,
    syncProveedor,
    busqueda,
    config.key,
    config.showFechasEstadia,
    config.entityLabelPlural,
    showToast,
  ]);

  useEffect(() => {
    if (selectedProject?.id) cargarListado();
  }, [selectedProject?.id, cargarListado]);

  const filasSinFechas = useMemo(
    () => (config.showFechasEstadia ? filas.filter((f) => f.sin_fechas_estadia) : []),
    [filas, config.showFechasEstadia]
  );

  const filasSincronizables = useMemo(
    () => filas.filter((f) => !esSyncExitoso(f) && puedeEncolarSync(f, config)),
    [filas, config]
  );

  const idsSincronizablesSeleccion = useMemo(() => {
    if (!seleccionados.length) return [];
    const setSel = new Set(seleccionados);
    return filas
      .filter((f) => setSel.has(f.asignacion_id) && !esSyncExitoso(f) && puedeEncolarSync(f, config))
      .map((f) => f.asignacion_id);
  }, [filas, seleccionados, config]);

  const sincronizarOperacion = async (row, forzarReenvio = false) => {
    if (esSyncExitoso(row) && !forzarReenvio) {
      showToast("warning", `El ${config.entityLabel} ya está sincronizado con Logiflex`);
      return;
    }
    if (!forzarReenvio && !row.cama_homologada) {
      showToast("warning", "La cama no tiene id_proveedor homologado con Logiflex");
      return;
    }
    if (!forzarReenvio && !puedeEncolarSync(row, config)) {
      showToast(
        "warning",
        bloqueoSincronizacionFila(row, config) ||
          `Esta asignación no puede sincronizarse como ${config.entityLabel}`
      );
      return;
    }
    setEnviandoSyncId(row.asignacion_id);
    try {
      const { data } = await alojamientoProveedorApi.encolarReserva(row.asignacion_id, {
        forzarReenvio: forzarReenvio || puedeReintentarSync(row) || estaEnColaSync(row),
        origen: "manual",
        tipoOperacion: config.tipoOperacion,
      });
      if (data.data?.omitido) {
        showToast("warning", data.data?.motivo || data.message || `No se encoló el ${config.entityLabel}`);
      } else {
        showToast(
          "success",
          data.message || `${config.entityLabel} encolado para sincronización con Logiflex`
        );
      }
      await cargarListado();
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al sincronizar con Logiflex"));
    } finally {
      setEnviandoSyncId(null);
    }
  };

  const sincronizarSeleccion = async () => {
    const ids = idsSincronizablesSeleccion.length
      ? idsSincronizablesSeleccion
      : filasSincronizables.map((f) => f.asignacion_id);
    if (!ids.length) {
      showToast("warning", `No hay ${config.entityLabelPlural} seleccionables para sincronizar`);
      return;
    }
    setSincronizandoLote(true);
    try {
      const { data } = await alojamientoProveedorApi.encolarBatch(ids, {
        forzarReenvio: false,
        origen: "manual",
        tipoOperacion: config.tipoOperacion,
      });
      showToast(
        "success",
        data.message || `${data.data?.encolados || ids.length} ${config.entityLabel}(s) encolado(s)`
      );
      await cargarListado();
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al sincronizar selección"));
    } finally {
      setSincronizandoLote(false);
    }
  };

  const abrirDetalleError = async (row) => {
    setModalErrorOpen(true);
    setErrorDetalle({ record: row, sync: null, logs: [] });
    setLoadingErrorDetalle(true);
    try {
      const { data } = await alojamientoProveedorApi.getLogsEnvio(
        row.asignacion_id,
        config.tipoOperacion
      );
      setErrorDetalle({
        record: row,
        sync: data.data?.sync || null,
        logs: data.data?.logs || [],
      });
    } catch (error) {
      showToast("error", extraerMensajeError(error, "Error al cargar detalle del envío"));
      setErrorDetalle({
        record: row,
        sync: row.respuesta_sync_proveedor
          ? { respuesta_proveedor: row.respuesta_sync_proveedor, intentos: row.intentos_sync }
          : null,
        logs: [],
      });
    } finally {
      setLoadingErrorDetalle(false);
    }
  };

  const exportarExcel = async () => {
    if (!filas.length) {
      showToast("warning", "No hay datos para exportar");
      return;
    }
    setExportando(true);
    try {
      const datos = filas.map((r) => ({
        "ID asignación": r.asignacion_id,
        DNI: r.dni,
        Trabajador: r.trabajador_nombre,
        Cargo: r.cargo,
        Régimen: r.regimen_codigo,
        Zona: r.zona_nombre,
        Módulo: r.modulo_nombre,
        Habitación: r.habitacion_numero,
        Cama: r.cama_codigo,
        "Estado asignación": r.estado_asignacion,
        Activo: r.activo ? "Sí" : "No",
        "Fecha reserva": r.fecha_reserva,
        "Check-in": r.fecha_check_in,
        "Check-out": r.fecha_check_out,
        "Inicio estadía": r.fecha_inicio_estadia || "",
        "Fin estadía": r.fecha_fin_estadia || "",
        "Sin fechas estadía": r.sin_fechas_estadia ? "Sí" : "No",
        "Cama homologada": r.cama_homologada ? "Sí" : "No",
        "ID cama Logiflex": r.cama_id_proveedor || "",
        "Puede sincronizar": puedeSincronizarFila(r, config) ? "Sí" : "No",
        "Motivo bloqueo sync": bloqueoSincronizacionFila(r, config) || "",
        "Sync Logiflex": r.sincronizada_proveedor ? "Sincronizada" : "No sincronizada",
        "Estado sync": r.estado_sync_proveedor || "",
        "Fecha sync": r.fecha_sync_proveedor || "",
        Intentos: r.intentos_sync ?? 0,
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(datos), config.excelSheet);
      const slug = (selectedProject?.nombre || "proyecto").replace(/[^\w]+/g, "_").slice(0, 25);
      XLSX.writeFile(wb, `${config.excelPrefix}_${slug}_${Date.now()}.xlsx`);
      showToast("success", `${filas.length} fila(s) exportada(s)`);
    } catch {
      showToast("error", "Error al exportar Excel");
    } finally {
      setExportando(false);
    }
  };

  const abrirEditarFechas = async (row) => {
    setEditRow(row);
    setFechaInicio(row.fecha_inicio_estadia || "");
    setFechaFin(row.fecha_fin_estadia || "");
    setMotivoModificacion("");
    setModalOpen(true);
    try {
      const { data } = await gestionAlojamientoApi.getPropuestaFechas(row.asignacion_id);
      if (data.data?.fecha_inicio_estadia) {
        setFechaInicio(data.data.fecha_inicio_estadia);
        setFechaFin(data.data.fecha_fin_estadia);
      }
    } catch {
      /* manual si no hay propuesta */
    }
  };

  const guardarFechas = async () => {
    if (!editRow || !fechaInicio || !fechaFin) {
      showToast("warning", "Ingrese fecha inicio y fin");
      return;
    }
    const cambioEntrada = fechaInicio !== (editRow.fecha_inicio_estadia || "");
    const cambioSalida = fechaFin !== (editRow.fecha_fin_estadia || "");
    if (!cambioEntrada && !cambioSalida) {
      showToast("warning", "No hay cambios en las fechas");
      return;
    }
    if (!motivoModificacion.trim()) {
      showToast("warning", "Indique el motivo del cambio (ej. adelanto de llegada)");
      return;
    }
    setGuardando(true);
    try {
      const { data } = await gestionAlojamientoApi.actualizarFechasEstadia(editRow.asignacion_id, {
        fecha_inicio_estadia: fechaInicio,
        fecha_fin_estadia: fechaFin,
        motivo: motivoModificacion.trim(),
      });
      showToast("success", data.message || "Fechas actualizadas y registradas en historial");
      setModalOpen(false);
      await cargarListado();
    } catch (error) {
      showToast("error", error.response?.data?.detail || "Error al guardar fechas");
    } finally {
      setGuardando(false);
    }
  };

  const completarFechasSeleccion = async () => {
    const ids = seleccionados.length
      ? seleccionados
      : filasSinFechas.map((f) => f.asignacion_id);
    if (!ids.length) {
      showToast("warning", "No hay asignaciones sin fechas para completar");
      return;
    }
    setCompletando(true);
    try {
      const { data } = await gestionAlojamientoApi.completarFechasEstadia({
        asignacion_ids: ids,
        usar_regimen: true,
        dias_default: 21,
      });
      showToast("success", data.message || "Proceso completado");
      await cargarListado();
    } catch (error) {
      showToast("error", error.response?.data?.detail || "Error al completar fechas");
    } finally {
      setCompletando(false);
    }
  };

  const columns = useMemo(() => {
    const base = [
      { title: "ID", dataIndex: "asignacion_id", width: 70 },
      { title: "DNI", dataIndex: "dni", width: 100 },
      { title: "Trabajador", dataIndex: "trabajador_nombre", ellipsis: true },
      {
        title: "Ubicación",
        key: "ubicacion",
        width: 160,
        ellipsis: true,
        render: (_, r) =>
          [r.zona_nombre, r.modulo_nombre, r.habitacion_numero, r.cama_codigo]
            .filter(Boolean)
            .join(" / ") || "—",
      },
      {
        title: "Estado",
        dataIndex: "estado_asignacion",
        width: 110,
        render: (v) => <Tag color={colorEstado(v)}>{v || "—"}</Tag>,
      },
      {
        title: "Activo",
        dataIndex: "activo",
        width: 72,
        align: "center",
        render: (v) => (
          <Tag color={v ? "success" : "default"}>{v ? "Sí" : "No"}</Tag>
        ),
      },
    ];

    if (config.showHomologacion) {
      base.push({
        title: "Homologación",
        key: "homologacion",
        width: 130,
        render: renderHomologacionCama,
      });
    }

    if (config.prereqColumn) {
      base.push({
        title: config.prereqLabel,
        key: "prereq",
        width: 110,
        align: "center",
        render: (_, row) => (
          <Tag color={row[config.prereqColumn] ? "success" : "warning"}>
            {row[config.prereqColumn] ? "OK" : "Pendiente"}
          </Tag>
        ),
      });
    }

    base.push({
      title: "Sync Logiflex",
      key: "sync_proveedor",
      width: 115,
      render: renderEstadoSyncProveedor,
    });

    if (config.showFechasEstadia) {
      base.push(
        {
          title: "Inicio estadía",
          dataIndex: "fecha_inicio_estadia",
          width: 108,
          render: (v) => (v ? v : <Tag color="warning">NULL</Tag>),
        },
        {
          title: "Fin estadía",
          dataIndex: "fecha_fin_estadia",
          width: 108,
          render: (v) => (v ? v : <Tag color="warning">NULL</Tag>),
        }
      );
    }

    base.push(
      {
        title: "F. reserva",
        dataIndex: "fecha_reserva",
        width: 120,
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: "Check-in",
        dataIndex: "fecha_check_in",
        width: 108,
        render: (v) => v || "—",
      },
      {
        title: "Check-out",
        dataIndex: "fecha_check_out",
        width: 108,
        render: (v) => v || "—",
      },
      {
        title: "Acciones",
        key: "accion",
        width: 118,
        className: "col-acciones-reserva",
        render: (_, row) => {
          const sinHomologar = !row.cama_homologada;
          const syncDeshabilitado =
            sincronizandoLote ||
            enviandoSyncId === row.asignacion_id ||
            sinHomologar ||
            (!esSyncExitoso(row) &&
              !estaEnColaSync(row) &&
              !puedeReintentarSync(row) &&
              !puedeSincronizarFila(row, config));
          const tituloSync = sinHomologar
            ? "Homologue la cama (id_proveedor) antes de sincronizar"
            : bloqueoSincronizacionFila(row, config) ||
              (esSyncExitoso(row) ? "Ya sincronizada" : "Sincronizar con Logiflex");

          return (
            <div className="gestion-reservas-acciones">
              {config.showFechasEstadia && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="btn-accion-fechas"
                  onClick={() => abrirEditarFechas(row)}
                  title="Editar fechas de estadía"
                >
                  <MdEdit size={14} />
                  <span>Fechas</span>
                </Button>
              )}
              {puedeVerErrorSync(row) && (
                <Button
                  size="sm"
                  className="btn-dh-secondary btn-accion-error"
                  disabled={sincronizandoLote}
                  onClick={() => abrirDetalleError(row)}
                  title="Ver detalle del error de sincronización"
                >
                  <MdErrorOutline size={14} />
                  <span>Error</span>
                </Button>
              )}
              {esSyncExitoso(row) ? (
                <span className="text-muted small text-center py-1">Sync OK</span>
              ) : estaEnColaSync(row) ? (
                <Button
                  size="sm"
                  className="btn-dh-primary btn-accion-sync"
                  disabled={
                    sincronizandoLote ||
                    enviandoSyncId === row.asignacion_id ||
                    sinHomologar
                  }
                  onClick={() => sincronizarOperacion(row, true)}
                  title={sinHomologar ? tituloSync : "Reencolar envío a Logiflex"}
                >
                  {enviandoSyncId === row.asignacion_id ? (
                    <Spin size="small" />
                  ) : (
                    <>
                      <MdCloudUpload size={14} />
                      <span>Reencolar</span>
                    </>
                  )}
                </Button>
              ) : (
                <Tooltip title={tituloSync}>
                  <span className="d-block w-100">
                    <Button
                      size="sm"
                      className="btn-dh-primary btn-accion-sync w-100"
                      disabled={syncDeshabilitado}
                      onClick={() => sincronizarOperacion(row, puedeReintentarSync(row))}
                    >
                      {enviandoSyncId === row.asignacion_id ? (
                        <Spin size="small" />
                      ) : (
                        <>
                          <MdCloudUpload size={14} />
                          <span>{puedeReintentarSync(row) ? "Reintentar" : "Sync"}</span>
                        </>
                      )}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      }
    );

    return base;
  }, [
    config,
    sincronizandoLote,
    enviandoSyncId,
    abrirEditarFechas,
    abrirDetalleError,
    sincronizarOperacion,
  ]);

  const logColumns = [
    { title: "Fecha", dataIndex: "created_at", key: "created_at", width: 150 },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 90,
      render: (estado) => (
        <Tag color={estado === "exitoso" ? "success" : "error"}>{estado}</Tag>
      ),
    },
    {
      title: "HTTP",
      dataIndex: "http_status",
      key: "http_status",
      width: 70,
      render: (v) => v ?? "—",
    },
    {
      title: "Respuesta",
      dataIndex: "response_body",
      key: "response_body",
      ellipsis: true,
      render: (v) => truncarTexto(v, 200) || "—",
    },
  ];

  if (!selectedProject?.id) {
    return (
      <div className="dh-reporte-bg min-vh-100 d-flex align-items-center justify-content-center">
        <style>
          {`
            .dh-reporte-bg {
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              min-height: 100vh;
            }
            .dh-alert-info {
              background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
              border: 1px solid #93c5fd;
              border-radius: 12px;
              padding: 1rem 1.25rem;
              color: #1e40af;
            }
          `}
        </style>
        <div className="dh-alert dh-alert-info mx-3">
          Seleccione un proyecto de campamento para continuar
        </div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding-bottom: 2rem;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover:not(:disabled) { color: white; opacity: 0.95; }
          .btn-dh-primary:disabled { opacity: 0.65; }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover:not(:disabled) { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control {
            border-radius: 10px;
            border-color: #e2e8f0;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.2);
          }
          .dh-alert-info {
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            border: 1px solid #93c5fd;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            color: #1e40af;
          }
          .stat-box {
            text-align: center;
            padding: 1rem;
            border-radius: 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
          }
          .stat-box h4 { margin: 0; font-weight: 700; color: #667eea; }
          .stat-box small { color: #64748b; }
          .stat-box.stat-warning h4 { color: #d97706; }
          .stat-box.stat-success h4 { color: #059669; }
          .stat-box.stat-danger h4 { color: #dc2626; }
          .gestion-reservas .resumen-bar {
            background: linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%);
            border-radius: 10px;
            padding: 12px 16px;
            margin-bottom: 16px;
            border: 1px solid #e2e8f0;
          }
          .gestion-reservas .resumen-bar strong { color: #667eea; }
          .gestion-reservas .ant-table-thead > tr > th {
            background: #f1f5f9 !important;
            color: #374151;
            font-weight: 600;
          }
          .gestion-reservas .ant-table-tbody > tr:hover > td {
            background: #f8fafc !important;
          }
          .dh-modal .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 15px 35px rgba(0,0,0,0.15);
          }
          .dh-modal .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.25rem 1.5rem;
          }
          .dh-modal .modal-title { font-weight: 700; }
          .dh-modal .btn-close { filter: invert(1); opacity: 0.9; }
          .dh-modal .modal-body { padding: 1.5rem; }
          .dh-modal .modal-footer {
            border-top: 1px solid #e2e8f0;
            padding: 1rem 1.5rem;
          }
          .dh-check-fechas .form-check-input:checked {
            background-color: #667eea;
            border-color: #667eea;
          }
          .gestion-reservas-acciones {
            display: flex;
            flex-direction: column;
            gap: 5px;
            min-width: 104px;
          }
          .gestion-reservas-acciones .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
            white-space: nowrap;
            font-size: 0.72rem;
            font-weight: 600;
            border-radius: 8px;
            padding: 4px 6px;
            width: 100%;
            line-height: 1.2;
          }
          .gestion-reservas-acciones .btn-accion-error {
            padding: 4px 6px;
            font-size: 0.72rem;
          }
          .gestion-reservas-acciones .btn-accion-fechas {
            border-color: #94a3b8;
            color: #475569;
            background: #fff;
          }
          .gestion-reservas-acciones .btn-accion-fechas:hover {
            background: #f1f5f9;
            border-color: #667eea;
            color: #667eea;
          }
          .gestion-reservas-acciones .btn-accion-sync {
            justify-content: center;
          }
          .gestion-reservas .col-acciones-reserva {
            background: #fafbfc !important;
            border-left: 2px solid #e2e8f0;
            vertical-align: top;
          }
          .gestion-reservas .celda-homologacion {
            line-height: 1.25;
          }
          .gestion-reservas .celda-id-proveedor {
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-family: ui-monospace, monospace;
            font-size: 0.7rem;
          }
          .gestion-reservas .log-response-box {
            background: #fff5f5;
            border: 1px solid #fecaca;
            border-radius: 8px;
            padding: 10px 12px;
            font-size: 0.8rem;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 180px;
            overflow-y: auto;
          }
          .gestion-reservas-tabla-wrap {
            overflow-x: auto;
          }
        `}
      </style>

      <ToastContainer />
      <div className="dh-main-header">
        <Container fluid="lg">
          <h1 className="dh-main-title d-flex align-items-center gap-2">
            <MdHotel size={32} />
            {config.title}
          </h1>
          <p className="dh-main-subtitle">
            Proyecto: {selectedProject.nombre} — {config.subtitle}
          </p>
        </Container>
      </div>

      <Container fluid="lg">
        {relacionActivo && (
          <Row className="mb-4">
            <Col>
              <div className="dh-alert dh-alert-info">
                <strong>Relación estado ↔ activo:</strong> {relacionActivo}
              </div>
            </Col>
          </Row>
        )}

        <Card className="dh-card mb-4">
          <Card.Header className="dh-card-header dh-card-header-primary">
            Filtros de búsqueda
          </Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={6} lg={3}>
                <Form.Label className="dh-form-label">Estado asignación</Form.Label>
                <Form.Select
                  className="dh-form-control"
                  value={estadoId}
                  onChange={(e) => setEstadoId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {estados.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} lg={2}>
                <Form.Label className="dh-form-label">Activo</Form.Label>
                <Form.Select
                  className="dh-form-control"
                  value={activoFiltro}
                  onChange={(e) => setActivoFiltro(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </Form.Select>
              </Col>
              <Col md={6} lg={3}>
                <Form.Label className="dh-form-label">Campo fecha</Form.Label>
                <Form.Select
                  className="dh-form-control"
                  value={campoFecha}
                  onChange={(e) => setCampoFecha(e.target.value)}
                >
                  {CAMPO_FECHA_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} lg={2}>
                <Form.Label className="dh-form-label">Desde</Form.Label>
                <Form.Control
                  type="date"
                  className="dh-form-control"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </Col>
              <Col md={6} lg={2}>
                <Form.Label className="dh-form-label">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  className="dh-form-control"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </Col>
              <Col md={6} lg={3}>
                <Form.Label className="dh-form-label">Sync proveedor</Form.Label>
                <Form.Select
                  className="dh-form-control"
                  value={syncProveedor}
                  onChange={(e) => setSyncProveedor(e.target.value)}
                >
                  {SYNC_PROVEEDOR_OPTS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={8} lg={4}>
                <Form.Label className="dh-form-label">Buscar</Form.Label>
                <Form.Control
                  type="text"
                  className="dh-form-control"
                  placeholder="DNI, nombre, cama o ID"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </Col>
              <Col md={4} lg={4} className="d-flex align-items-end">
                {config.showFechasEstadia && (
                  <Form.Check
                    type="checkbox"
                    id="soloSinFechas"
                    className="dh-check-fechas dh-form-label mb-2"
                    label="Solo sin fechas inicio/fin estadía"
                    checked={soloSinFechas}
                    onChange={(e) => setSoloSinFechas(e.target.checked)}
                  />
                )}
              </Col>
              <Col md={12} lg={4} className="d-flex align-items-end">
                <Button
                  className="btn-dh-primary w-100"
                  onClick={cargarListado}
                  disabled={loading}
                >
                  Buscar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="mb-4 g-3">
          {[
            ["Total", resumen.total, ""],
            ["Activas", resumen.activas, ""],
            ["Inactivas", resumen.inactivas, ""],
            ...(config.showFechasEstadia
              ? [["Sin fechas estadía", resumen.sin_fechas_estadia, "stat-warning"]]
              : []),
            ["Sync Logiflex OK", resumen.sincronizadas_proveedor, "stat-success"],
            ["Sin sync Logiflex", resumen.no_sincronizadas_proveedor, "stat-danger"],
            ...(config.showHomologacion
              ? [["Sin homologar", resumen.sin_homologar ?? 0, "stat-warning"]]
              : []),
          ].map(([label, val, extraClass]) => (
            <Col key={label} xs={6} md={4} lg={2}>
              <div className={`stat-box ${extraClass}`}>
                <h4>{val}</h4>
                <small>{label}</small>
              </div>
            </Col>
          ))}
        </Row>

        <Card className="dh-card">
          <Card.Header className="dh-card-header dh-card-header-success d-flex flex-wrap justify-content-between align-items-center gap-2">
            <span>Resultados</span>
            <Space wrap size="small">
              <Button
                size="sm"
                className="btn-dh-secondary"
                onClick={() => cargarListado()}
                disabled={loading}
              >
                <MdRefresh className="me-1" />
                Actualizar
              </Button>
              <Button
                size="sm"
                className="btn-dh-secondary"
                onClick={exportarExcel}
                disabled={!filas.length || exportando}
              >
                <MdDownload className="me-1" />
                {exportando ? "Exportando…" : "Excel"}
              </Button>
              <Tooltip
                title={
                  seleccionados.length
                    ? `Sincronizar ${idsSincronizablesSeleccion.length} seleccionada(s) con Logiflex`
                    : `Sincronizar visibles pendientes (${filasSincronizables.length})`
                }
              >
                <Button
                  size="sm"
                  className="btn-dh-primary"
                  onClick={sincronizarSeleccion}
                  disabled={
                    sincronizandoLote ||
                    (!idsSincronizablesSeleccion.length && !filasSincronizables.length)
                  }
                >
                  <MdCloudUpload className="me-1" />
                  {sincronizandoLote ? "Sincronizando…" : "Sync selección"}
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  config.showFechasEstadia
                    ? seleccionados.length
                      ? `Completar ${seleccionados.length} seleccionada(s)`
                      : `Completar visibles sin fechas (${filasSinFechas.length})`
                    : ""
                }
              >
                {config.showFechasEstadia && (
                  <Button
                    size="sm"
                    className="btn-dh-primary"
                    onClick={completarFechasSeleccion}
                    disabled={completando || (!seleccionados.length && !filasSinFechas.length)}
                  >
                    <MdAutoFixHigh className="me-1" />
                    {completando ? "Completando…" : "Completar fechas"}
                  </Button>
                )}
              </Tooltip>
            </Space>
          </Card.Header>
          <Card.Body>
            <div className="gestion-reservas">
              <div className="resumen-bar small text-muted mb-3">
                {config.resumenHint}
              </div>
              <div className="dh-alert dh-alert-info mb-3 small">
                <strong>Sincronización Logiflex ({config.entityLabel}):</strong> {config.syncHelpText}
              </div>
              <Spin spinning={loading}>
                <div className="gestion-reservas-tabla-wrap">
                  <Table
                    rowKey="asignacion_id"
                    columns={columns}
                    dataSource={filas}
                    size="small"
                    scroll={{ x: "max-content" }}
                    pagination={{ pageSize: 50, showSizeChanger: true }}
                    locale={{ emptyText: config.emptyText }}
                    rowSelection={{
                      selectedRowKeys: seleccionados,
                      onChange: (keys) => setSeleccionados(keys),
                    }}
                  />
                </div>
              </Spin>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {config.showFechasEstadia && (
      <Modal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        centered
        className="dh-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Fechas de estadía — ID {editRow?.asignacion_id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            {editRow?.trabajador_nombre} · DNI {editRow?.dni}
          </p>
          <p className="text-muted small mb-3">
            Use esta opción para adelantos de llegada u otros ajustes. Cada cambio queda
            registrado con fecha, usuario y si se modificó la entrada y/o la salida.
          </p>
          {editRow && (
            <div className="small text-muted mb-3">
              Actual: inicio <strong>{editRow.fecha_inicio_estadia || "—"}</strong> · fin{" "}
              <strong>{editRow.fecha_fin_estadia || "—"}</strong>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="dh-form-label">Fecha inicio estadía</Form.Label>
            <Form.Control
              type="date"
              className="dh-form-control"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="dh-form-label">Fecha fin estadía</Form.Label>
            <Form.Control
              type="date"
              className="dh-form-control"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="dh-form-label">Motivo del cambio *</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="dh-form-control"
              placeholder="Ej. Adelanto de llegada del trabajador"
              value={motivoModificacion}
              onChange={(e) => setMotivoModificacion(e.target.value)}
              maxLength={500}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-dh-secondary" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
          <Button className="btn-dh-primary" onClick={guardarFechas} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar y registrar"}
          </Button>
        </Modal.Footer>
      </Modal>
      )}

      <AntModal
        title={
          <span className="d-flex align-items-center gap-2">
            <MdErrorOutline color="#dc2626" />
            Error de sincronización — {errorDetalle?.record?.trabajador_nombre || config.entityLabel}
          </span>
        }
        open={modalErrorOpen}
        onCancel={() => setModalErrorOpen(false)}
        footer={
          <Button className="btn-dh-secondary" onClick={() => setModalErrorOpen(false)}>
            Cerrar
          </Button>
        }
        width={780}
        destroyOnClose
        className="dh-modal-ant"
      >
        {loadingErrorDetalle ? (
          <div className="text-center py-4">
            <Spin tip="Cargando historial de envío…" />
          </div>
        ) : (
          <>
            <Descriptions bordered size="small" column={2} className="mb-3">
              <Descriptions.Item label="ID asignación">
                {errorDetalle?.record?.asignacion_id || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="DNI">{errorDetalle?.record?.dni || "—"}</Descriptions.Item>
              <Descriptions.Item label="Cama homologada">
                {errorDetalle?.record?.cama_homologada ? (
                  <Tag color="success">{errorDetalle?.record?.cama_id_proveedor}</Tag>
                ) : (
                  <Tag color="warning">Sin id_proveedor</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Estado sync">
                <Tag color="error">{errorDetalle?.sync?.estado_envio || errorDetalle?.record?.estado_sync_proveedor || "error"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Intentos">
                {errorDetalle?.sync?.intentos ?? errorDetalle?.record?.intentos_sync ?? 0}
              </Descriptions.Item>
            </Descriptions>

            {(errorDetalle?.sync?.respuesta_proveedor ||
              errorDetalle?.record?.respuesta_sync_proveedor) && (
              <div className="mb-3">
                <div className="small fw-semibold mb-1">Respuesta del proveedor</div>
                <div className="log-response-box">
                  {errorDetalle?.sync?.respuesta_proveedor ||
                    errorDetalle?.record?.respuesta_sync_proveedor}
                </div>
              </div>
            )}

            <div className="small fw-semibold mb-2">
              Historial de intentos ({errorDetalle?.logs?.length || 0})
            </div>
            <Table
              rowKey="id"
              size="small"
              columns={logColumns}
              dataSource={errorDetalle?.logs || []}
              pagination={false}
              locale={{ emptyText: "Sin registros de envío" }}
            />
          </>
        )}
      </AntModal>
    </div>
  );
};

export default GestionAlojamientoLogiflex;
